import { useRef, useState } from "react";
import { CELDA_VACIA } from "../consts";

// Campos de la row que no son celdas de día.
const CAMPOS_FIJOS = ["id", "empleado"];

const indexarPorId = (lista) =>
    lista.reduce((acc, row) => {
        acc[row.id] = row;
        return acc;
    }, {});

// Compara contra el último estado guardado (no contra la edición inmediatamente anterior),
// así dos ediciones seguidas de la misma celda siguen comparando "antes" contra el original.
export function useHorariosDraft(initialRows) {
    const [rows, setRows] = useState(initialRows);
    const [cambiosPendientes, setCambiosPendientes] = useState({});
    // Último estado guardado (carga inicial o último guardado exitoso).
    const savedRowsRef = useRef(indexarPorId(initialRows));
    const savedListRef = useRef(initialRows);
    // Última versión de rows y del mapa de cambios, actualizadas de forma sincrónica.
    // Necesario porque el commit por blur de una celda en edición puede dispararse en el
    // mismo click que "Guardar cambios": el estado de React todavía no re-renderizó,
    // pero el ref ya tiene el cambio incluido.
    const ultimasRowsRef = useRef(initialRows);
    const cambiosRef = useRef({});

    // Reemplaza los datos de la grilla tras un fetch (cambio de mes/hospital):
    // resetea el estado guardado de referencia y descarta los cambios pendientes.
    const loadRows = (newRows) => {
        savedRowsRef.current = indexarPorId(newRows);
        savedListRef.current = newRows;
        ultimasRowsRef.current = newRows;
        cambiosRef.current = {};
        setRows(newRows);
        setCambiosPendientes({});
    };

    const processRowUpdate = (newRow) => {
        const savedRow = savedRowsRef.current[newRow.id];

        const next = { ...cambiosRef.current };
        const camposDia = Object.keys(newRow).filter((key) => !CAMPOS_FIJOS.includes(key));
        camposDia.forEach((key) => {
            const antes = savedRow?.[key] ?? CELDA_VACIA;
            const despues = newRow[key] ?? CELDA_VACIA;
            const cambioKey = `${newRow.id}-${key}`;
            if (JSON.stringify(antes) !== JSON.stringify(despues)) {
                next[cambioKey] = { antes, despues };
            } else {
                delete next[cambioKey];
            }
        });
        cambiosRef.current = next;
        setCambiosPendientes(next);

        ultimasRowsRef.current = ultimasRowsRef.current.map((row) => (row.id === newRow.id ? newRow : row));
        setRows(ultimasRowsRef.current);

        return newRow;
    };

    // Lectura sincrónica de los cambios (para el guardado: incluye el commit del mismo click).
    const getCambiosPendientes = () => cambiosRef.current;

    // Tras un guardado exitoso: lo que está en pantalla pasa a ser el nuevo estado base.
    const marcarGuardado = () => {
        savedRowsRef.current = indexarPorId(ultimasRowsRef.current);
        savedListRef.current = ultimasRowsRef.current;
        cambiosRef.current = {};
        setRows(ultimasRowsRef.current);
        setCambiosPendientes({});
    };

    // Restaura la grilla al último estado guardado.
    const descartarCambios = () => {
        ultimasRowsRef.current = savedListRef.current;
        cambiosRef.current = {};
        setRows(savedListRef.current);
        setCambiosPendientes({});
    };

    const hayCambios = Object.keys(cambiosPendientes).length > 0;

    return {
        rows,
        cambiosPendientes,
        getCambiosPendientes,
        processRowUpdate,
        hayCambios,
        loadRows,
        marcarGuardado,
        descartarCambios,
    };
}
